import Button from '@mui/material/Button';
import Tooltip from '@mui/material/Tooltip';
import { WorkbookSheetRowCell } from '@progress/kendo-ooxml';
import { ExcelExportColumn, ExcelExport as Export } from '@progress/kendo-react-excel-export';
import { TFunction } from 'i18next';
import * as React from 'react';

export interface TableHeader {
  key: string;
  label: string;
}
export type TableHeaders = TableHeader[];

export type TableHeadersInput = TableHeaders | TableHeaders[];

const isArrayOfTableHeaderArrays = (headers: TableHeadersInput): headers is TableHeaders[] => {
  return headers.length > 0 && Array.isArray(headers[0]);
};

export interface TableRow {
  [key: string]: Date | number | string | boolean | WorkbookSheetRowCell;
}
export type TableRows = Array<Record<string, any>>;

export interface ExcelTab {
  name: string;
  data: TableRows;
}
export type ExcelTabs = ExcelTab[];

interface Props {
  data: ExcelTabs | undefined;
  headers: TableHeadersInput | undefined;
  name: string;
  t: TFunction;
  style?: React.CSSProperties;
  fullWidth?: boolean;
  buttonText?: string;
  id?: string;
}

class ExportExcelButton extends React.Component<Props, {}> {
  render() {
    const { data, name, t, style, fullWidth, buttonText, headers } = this.props;

    return (
      <ExportExcel data={data ?? []} name={name} headers={headers}>
        <Tooltip
          title={t(['shared:exportExcelTooltio'], {
            defaultValue: 'Export data to Excel. Only data displayed above is exported.',
          })}
        >
          <Button
            id={this.props.id}
            style={{ margin: '10px', ...style }}
            variant="contained"
            color="primary"
            fullWidth={fullWidth}
          >
            {buttonText ?? t(['shared:exportExcel'], { defaultValue: 'Export Excel' })}
          </Button>
        </Tooltip>
      </ExportExcel>
    );
  }
}

export default ExportExcelButton;

// NOTE: Including spacing between lines
const fontSize = 20;

export class ExportExcel extends React.Component<Omit<Props, 't'>, {}> {
  _exporters: Array<Export | null> = [];

  export = () => {
    const component = this._exporters[0];
    if (!component) {
      return;
    }
    const options = component.workbookOptions();

    this._exporters.forEach((exp, i) => {
      if (!exp) {
        return;
      }

      const additionalSheetOptions = exp.workbookOptions();

      if (!options.sheets || !additionalSheetOptions.sheets) {
        return;
      }

      options.sheets[i] = additionalSheetOptions.sheets[0];
      options.sheets[i].name = this.props.data?.[i]?.name;

      const rows = options.sheets[i].rows || [];
      const cols = options.sheets[i].columns || [];

      // Check each cell in each row for objects and transform them.
      rows.forEach((row) => {
        const lines = Math.max(
          ...(row.cells || []).map((c) => (typeof c.value === 'string' ? c.value.match(/\n/g)?.length || 1 : 1)),
        );

        row.height = fontSize * lines;

        if (row.type === 'data') {
          (row.cells || []).forEach((cell, i) => {
            cell.verticalAlign = 'top';
            cell.wrap = true;

            if (cell.value instanceof Date) {
              cell.format = 'YYYY/mm/dd hh:mm:ss';
              cols[i].autoWidth = false;
              cols[i].width = cell.format.length * 8;
            }

            if (typeof cell.value === 'object' && cell.value !== null) {
              const obj: WorkbookSheetRowCell = { ...(cell.value as WorkbookSheetRowCell) };
              Object.keys(obj).map((key) => {
                (cell as any)[key] = (obj as any)[key];
              });
            }
          });
        }
      });
    });

    component.save(options);
  };

  render() {
    const { data, name, children, style, headers } = this.props;

    return (
      <span onClick={this.export} style={{ ...style }}>
        {children}
        {data?.map((d, i) => {
          const sheetHeaders = headers && isArrayOfTableHeaderArrays(headers) ? headers[i] : headers;
          return (
            <Export
              data={d?.data ?? []}
              key={`${i}-export`}
              fileName={`${name.toLocaleLowerCase()}.xlsx`}
              ref={(exporter) => (this._exporters[i] = exporter)}
            >
              {sheetHeaders?.map((header) => (
                <ExcelExportColumn key={`${header.key}`} field={header.key} title={header.label ?? header.key} />
              ))}
            </Export>
          );
        })}
      </span>
    );
  }
}
